// Sector route map: depth columns left → right, lanes vertically. After a room
// the reachable next nodes pulse and can be picked (safer repairs, more loot or
// a dangerous elite). X / Esc closes it and returns to the room. Tab opens the same
// map view-only.

import Phaser from 'phaser';
import { iconKey } from '../art/keys';
import { SCENES } from '../config/constants';
import { COLORS } from '../config/palette';
import type { RouteSceneData } from '../core/contracts';
import type { NodeType, RouteNode } from '../core/types';
import { MenuNav, Panel, TYPE, UI, backdrop, createButton, footerHints, iconImage, setMenuCursor, sfx, txt } from '../ui/kit';

const NODE_INFO: Record<NodeType, { label: string; hint: string; color: number }> = {
  start: { label: 'DROP ZONE', hint: 'Sector entry.', color: COLORS.uiDim },
  combat: { label: 'COMBAT', hint: 'Standard arena. Scrap and an orb.', color: COLORS.uiText },
  elite: { label: 'ELITE', hint: 'Dangerous. Choose one of three rewards.', color: COLORS.threat },
  loot: { label: 'LOOT', hint: 'Guarded vault with two orbs.', color: COLORS.orbArsenal },
  repair: { label: 'REPAIR', hint: 'Safer. Restore 40% hull.', color: COLORS.heal },
  station: { label: 'STATION', hint: 'Shop, repair and reroll.', color: COLORS.advanced },
  boss: { label: 'BOSS', hint: 'Sector guardian.', color: COLORS.threatHot },
};

export class RouteScene extends Phaser.Scene {
  private nav!: MenuNav;
  private routeData!: RouteSceneData;
  private picked = false;

  constructor() {
    super(SCENES.ROUTE);
  }

  create(data: RouteSceneData): void {
    this.routeData = data;
    this.picked = false;
    setMenuCursor(this, true);
    backdrop(this, 0.86);

    const { graph, currentId, viewOnly } = data;
    const nodes = Object.values(graph.nodes);
    const current = graph.nodes[currentId];
    const next = new Set(viewOnly ? [] : current.next);

    const pw = 1640;
    const ph = 820;
    const px = (UI.W - pw) / 2;
    const py = (UI.H - ph) / 2 - 20;
    new Panel(this, px, py, pw, ph, { fill: COLORS.uiBg, fillAlpha: 0.95, accent: COLORS.volt, ticks: true, chamfer: 22, header: 96, headerAlpha: 0.1, scanlines: true });
    txt(this, px + 52, py + 22, data.sectorName, { font: 'display', size: TYPE.h1, weight: 700, spacing: 6 });
    const closable = viewOnly || !!data.onClose;
    txt(this, px + pw - (closable ? 120 : 52), py + 40, viewOnly ? 'SECTOR MAP' : 'CHOOSE YOUR ROUTE', { font: 'display', size: TYPE.label, color: viewOnly ? COLORS.uiDim : COLORS.volt, spacing: 4, originX: 1 });

    const depths = graph.depthCount;
    const lanes = Math.max(1, graph.lanes);
    const x0 = px + 140;
    const x1 = px + pw - 140;
    const yMid = py + 400;
    const laneGap = 170;
    const pos = (n: RouteNode) => ({
      x: x0 + ((x1 - x0) * n.depth) / Math.max(1, depths - 1),
      y: yMid + (n.lane - (lanes - 1) / 2) * laneGap,
    });

    // Edges.
    const g = this.add.graphics();
    for (const n of nodes) {
      const a = pos(n);
      for (const id of n.next) {
        const m = graph.nodes[id];
        if (!m) continue;
        const b = pos(m);
        const travelled = n.visited && m.visited;
        const option = n.id === currentId && next.has(id);
        g.lineStyle(option ? 4 : travelled ? 4 : 2, option ? COLORS.volt : travelled ? COLORS.voltGlow : COLORS.uiBorder, option || travelled ? 0.9 : 0.6);
        g.lineBetween(a.x, a.y, b.x, b.y);
      }
    }

    this.nav = new MenuNav(this, {
      layout: 'spatial',
      onBack: closable ? () => this.close() : undefined,
      keys: viewOnly ? { Tab: () => this.close(), Escape: () => this.close() } : closable ? { Escape: () => this.close() } : undefined,
    });
    if (closable) {
      // Header close button (not in the nav ring so arrows stay on the route options).
      createButton(this, px + pw - 62, py + 48, { label: 'X', width: 56, height: 56, align: 'center', accent: COLORS.uiDim, sound: null, onClick: () => this.close() });
    }

    let firstOption: ReturnType<typeof createButton> | null = null;
    for (const n of nodes) {
      const p = pos(n);
      const info = NODE_INFO[n.type];
      const isCurrent = n.id === currentId;
      if (next.has(n.id)) {
        const btn = createButton(this, p.x, p.y, { label: info.label, width: 230, height: 64, align: 'center', accent: info.color, icon: iconKey.node(n.type), sound: 'ui_confirm', onClick: () => this.pick(n.id) }, this.nav);
        this.tweens.add({ targets: btn, scale: 1.06, duration: 520, yoyo: true, repeat: -1, ease: 'Sine.InOut' });
        txt(this, p.x, p.y + 48, info.hint, { size: TYPE.micro, color: COLORS.uiDim, originX: 0.5, wrap: 230, align: 'center' });
        firstOption ??= btn;
      } else {
        const ring = this.add.graphics();
        const r = isCurrent ? 38 : 30;
        ring.fillStyle(COLORS.uiPanel, 1).fillCircle(p.x, p.y, r);
        ring.lineStyle(isCurrent ? 4 : 2, isCurrent ? COLORS.volt : n.visited ? COLORS.voltGlow : info.color, isCurrent ? 1 : n.visited ? 0.7 : 0.5).strokeCircle(p.x, p.y, r);
        iconImage(this, p.x, p.y, iconKey.node(n.type), isCurrent ? 40 : 32, n.visited || isCurrent ? COLORS.uiText : info.color).setAlpha(n.visited || isCurrent ? 1 : 0.75);
        if (isCurrent) txt(this, p.x, p.y - 62, 'YOU ARE HERE', { font: 'display', size: TYPE.micro, color: COLORS.volt, originX: 0.5, spacing: 2 });
        if (n.type === 'boss' || n.type === 'station') txt(this, p.x, p.y + 46, info.label, { font: 'display', size: TYPE.micro, color: info.color, originX: 0.5, spacing: 2 });
      }
    }

    // Legend.
    const ly = py + ph - 70;
    const legend: NodeType[] = ['repair', 'loot', 'elite', 'combat', 'station'];
    legend.forEach((t, i) => {
      const lx = px + 80 + i * 300;
      iconImage(this, lx, ly, iconKey.node(t), 30, NODE_INFO[t].color);
      txt(this, lx + 26, ly - 12, `${NODE_INFO[t].label} — ${t === 'repair' ? 'safer' : t === 'loot' ? 'more loot' : t === 'elite' ? 'dangerous' : t === 'combat' ? 'standard' : 'shop'}`, { size: TYPE.micro, color: COLORS.uiDim });
    });

    if (viewOnly) {
      createButton(this, UI.W / 2, py + ph + 20, { label: 'Close map', width: 300, height: 56, align: 'center', sound: 'ui_back', onClick: () => this.close() }, this.nav);
      footerHints(this, [{ action: 'back', label: 'Close' }]);
    } else {
      footerHints(this, [
        { action: 'navigateH', label: 'Select' },
        { action: 'confirm', label: 'Travel' },
        ...(closable ? [{ action: 'back' as const, label: 'Close' }] : []),
      ]);
    }
    if (firstOption) this.nav.focus(firstOption, { silent: true });
    this.nav.lock(250);
    this.cameras.main.fadeIn(180, 5, 7, 11);
  }

  update(): void {
    this.nav?.update();
  }

  private pick(nodeId: string): void {
    if (this.picked) return;
    this.picked = true;
    sfx('ui_confirm');
    this.routeData.onPick(nodeId);
  }

  private close(): void {
    if (this.picked) return;
    this.picked = true;
    sfx('ui_back');
    if (this.routeData.onClose) this.routeData.onClose();
    else this.scene.stop();
  }
}
